import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Attempt, OrderDetail, OrderStatus } from '@/api/types';
import { Card, ErrorNote, Field, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { StatusPill } from '@/components/StatusPill';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const { can, isPlatformStaff } = useAuth();
  const queryClient = useQueryClient();
  const [resolving, setResolving] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<OrderDetail>(`/api/admin/orders/${id}`),
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<{ outcome: string; status: string }>(
      `/api/admin/orders/${id}/reconcile`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['order', id] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const { order, attempts } = data;

  return (
    <div className="space-y-4">
      <Card
        title={
          <span className="flex items-center gap-3">
            <Reference value={order.agentReference} />
            <StatusPill status={order.status} />
            {order.canary && (
              <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-800">
                CANARY
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex gap-2">
            {isPlatformStaff && can('ADMIN', 'OPS', 'SUPPORT') && (
              <button type="button" className="btn-secondary"
                      disabled={reconcile.isPending}
                      onClick={() => reconcile.mutate()}>
                {reconcile.isPending ? 'Reconciling…' : 'Force reconcile'}
              </button>
            )}
            {isPlatformStaff && can('ADMIN', 'OPS') && (
              <button type="button" className="btn-danger" onClick={() => setResolving(true)}>
                Resolve manually
              </button>
            )}
          </div>
        }
      >
        {reconcile.isError && <ErrorNote error={reconcile.error} />}
        {reconcile.isSuccess && (
          <p className="m-4 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Reconciliation returned <strong>{reconcile.data.outcome}</strong>; the order is now{' '}
            <strong>{reconcile.data.status}</strong>.
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4">
          <Field label="Merchant">{order.merchantCode}</Field>
          <Field label="Client reference">
            <Reference value={order.clientReference} />
          </Field>
          <Field label="Order type">{order.orderType}</Field>
          <Field label="Product">
            {order.productId}{order.productCode ? ` · ${order.productCode}` : ''}
          </Field>
          <Field label="Target"><span className="mono">{order.target}</span></Field>
          <Field label="Amount">
            <Money amount={money(order.amount)} currency={order.currency} />
          </Field>
          <Field label="Upstream recharge id">
            <span className="tabular">{order.upstreamRechargeId ?? '—'}</span>
          </Field>
          <Field label="Placed"><Timestamp value={order.createdAt} /></Field>
          <Field label="Completed"><Timestamp value={order.completedAt} /></Field>
          <Field label="Reconcile attempts">
            <span className="tabular">{order.reconcileAttempts}</span>
          </Field>
          <Field label="Not-found streak">
            <span className="tabular">{order.notFoundStreak}</span>
            <span className="ml-1 text-xs text-ink-500">of 3 needed to reverse</span>
          </Field>
          <Field label="Balance corroboration">
            {order.balanceCorroboration
              ? <span title="Advisory only — it never overrides the query endpoint">
                  {order.balanceCorroboration}
                </span>
              : '—'}
          </Field>
        </div>

        {order.message && (
          <div className="border-t border-ink-100 px-4 py-3">
            <div className="label">Upstream message</div>
            <p className="mt-1 text-sm text-ink-800">{order.message}</p>
          </div>
        )}

        {data.openCaseNumber && (
          <div className="border-t border-ink-100 bg-red-50 px-4 py-3 text-sm text-red-900">
            Open exception case <strong>{data.openCaseNumber}</strong> ·{' '}
            <Link to="/exceptions" className="underline">go to the queue</Link>
          </div>
        )}
      </Card>

      {Object.keys(data.payments).length > 0 && (
        <Card title="Payments">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th">Method</th><th className="th">Status</th>
                <th className="th text-right">Amount</th><th className="th">External reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {Object.entries(data.payments).map(([paymentId, payment]) => (
                <tr key={paymentId}>
                  <td className="td">{payment.method}</td>
                  <td className="td">{payment.status}</td>
                  <td className="td text-right">
                    <Money amount={money(payment.amount)} currency={payment.currency} />
                  </td>
                  <td className="td mono">{payment.externalRef || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      <Card title={`Attempt trail — ${attempts.length} call${attempts.length === 1 ? '' : 's'}`}>
        {attempts.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-ink-500">
            Nothing was sent upstream for this order.
          </p>
        )}
        <ol className="divide-y divide-ink-100">
          {attempts.map((attempt) => <AttemptRow key={attempt.attemptNo} attempt={attempt} />)}
        </ol>
      </Card>

      {resolving && (
        <ResolveModal orderId={id} currentStatus={order.status} onClose={() => setResolving(false)} />
      )}
    </div>
  );
}

function AttemptRow({ attempt }: { attempt: Attempt }) {
  const [open, setOpen] = useState(false);
  const failed = attempt.outcome !== 'SUCCESS';

  return (
    <li className="px-4 py-3">
      <button type="button" className="flex w-full items-center gap-3 text-left"
              onClick={() => setOpen((value) => !value)}>
        <span className="tabular w-6 text-xs text-ink-400">#{attempt.attemptNo}</span>
        <span className="mono flex-1 truncate">{attempt.endpoint}</span>
        <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
          failed ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
        }`}>
          {attempt.outcome}
        </span>
        <span className="tabular w-14 text-right text-xs text-ink-500">
          {attempt.httpStatus ?? '—'}
        </span>
        <span className="tabular w-16 text-right text-xs text-ink-500">
          {attempt.durationMs ?? '—'}ms
        </span>
        <span className="w-44 text-right text-xs"><Timestamp value={attempt.startedAt} /></span>
        <span className="w-4 text-ink-400">{open ? '▾' : '▸'}</span>
      </button>

      {attempt.errorMessage && (
        <p className="mt-1 pl-9 text-xs text-red-700">{attempt.errorMessage}</p>
      )}

      {open && (
        <div className="mt-3 grid grid-cols-1 gap-3 pl-9 lg:grid-cols-2">
          <PayloadBlock title="Request" payload={attempt.requestBody} />
          <PayloadBlock title="Response" payload={attempt.responseBody} />
        </div>
      )}
    </li>
  );
}

function PayloadBlock({ title, payload }: { title: string; payload: string | null }) {
  let pretty = payload ?? '';
  try {
    if (payload) pretty = JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    // Not JSON — show it as it was stored rather than hiding it.
  }
  return (
    <div>
      <div className="label">{title}</div>
      <pre className="mt-1 max-h-72 overflow-auto rounded bg-ink-950 p-3 text-[11px] leading-relaxed text-ink-100">
        {pretty || '—'}
      </pre>
    </div>
  );
}

const RESOLVABLE: OrderStatus[] = ['SUCCEEDED', 'REVERSED', 'FAILED', 'NEEDS_MANUAL_REVIEW'];

function ResolveModal({ orderId, currentStatus, onClose }: {
  orderId: string; currentStatus: OrderStatus; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<OrderStatus>('SUCCEEDED');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');

  const resolve = useMutation({
    mutationFn: () => api.post(`/api/admin/orders/${orderId}/resolve`, {
      status, reason, evidenceReference: evidence || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      onClose();
    },
  });

  return (
    <Modal title="Resolve this order by hand" onClose={onClose}>
      <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        This overrides what the upstream told us. It fires the merchant webhook and is written to
        the audit log under your name. The order is currently <strong>{currentStatus}</strong>.
      </p>

      <label className="block">
        <span className="label">New status</span>
        <select className="input mt-1" value={status}
                onChange={(e) => setStatus(e.target.value as OrderStatus)}>
          {RESOLVABLE.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>

      {status === 'SUCCEEDED' && (
        <label className="mt-3 block">
          <span className="label">Evidence reference</span>
          <input className="input mt-1" value={evidence} required
                 placeholder="HOT ticket or portal screenshot reference"
                 onChange={(e) => setEvidence(e.target.value)} />
          <span className="mt-1 block text-xs text-ink-500">
            Required. Marking an order delivered on conviction alone is how a real loss becomes an
            invisible one.
          </span>
        </label>
      )}

      <label className="mt-3 block">
        <span className="label">Reason</span>
        <textarea className="input mt-1" rows={3} value={reason} required
                  placeholder="What did you check, and what did it show?"
                  onChange={(e) => setReason(e.target.value)} />
      </label>

      {resolve.isError && <ErrorNote error={resolve.error} />}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-danger"
                disabled={!reason.trim() || (status === 'SUCCEEDED' && !evidence.trim())
                          || resolve.isPending}
                onClick={() => resolve.mutate()}>
          {resolve.isPending ? 'Saving…' : `Set ${status}`}
        </button>
      </div>
    </Modal>
  );
}
